"use client";
import {
  emailRequirements,
  notBlankTextRequirements,
} from "@/app/(core)/register/types";
import { useMainMetaMap } from "@/app/components/CoreDataProvider";
import {
  Box,
  Button,
  MantineTheme,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { handleResponseError } from "../../../../../lib/strapi/strapiApi";
import { FormType } from "../../../../api/strapi/sendCustomerEmail/route";
import { setNotification } from "../../../../components/notifications";
import FieldLabel from "../../../profile/components/inputs/FieldLabel";
import FieldMultiChipValue from "../../../profile/components/inputs/FieldMultiChipValue";
import FieldNumberValue from "../../../profile/components/inputs/FieldNumberValue";
import FieldTextValue from "../../../profile/components/inputs/FieldTextValue";
import FieldTextareaValue from "../../../profile/components/inputs/FieldTextareaValue";

export default function ApplyForm({
  subjectNames,
}: {
  subjectNames: string[];
}) {
  const mainMetaMap = useMainMetaMap();
  const { data: session } = useSession();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const theme = useMantineTheme();
  const form = useForm({
    initialValues: {
      name: session?.user.name ?? "",
      email: session?.user.email ?? "",
      subjects:
        session?.user.relatedSubjectsStudied?.map(
          (subject) => mainMetaMap[subject.documentId]?.title
        ) ?? [],
      score: "",
      years: "",
      application: "",
    },
    validate: (values) => {
      return {
        name: notBlankTextRequirements.test(values.name)
          ? null
          : "Enter a valid name",
        email: emailRequirements.test(values.email) ? null : "Invalid email",
        subjects:
          values.subjects.length !== 0 ? null : "Select at least one subject",
        score:
          Number(values.score) > 0 && Number(values.score) <= 45
            ? Number(values.score) <= 35
              ? "Sorry, we require a higher IB score!"
              : null
            : "Input a valid IB score",
        years:
          Number(values.years) >= 3
            ? null
            : Number(values.years) > 0
            ? "Sorry, we require more experience!"
            : "Input a valid IB score",
        application: notBlankTextRequirements.test(values.application)
          ? values.application.length > 700
            ? null
            : "Please enter a more detailed application"
          : "Enter a valid application message",
      };
    },
    validateInputOnChange: [
      "name",
      "email",
      "application",
      "score",
      "years",
      "subjects",
    ],
    transformValues: (
      values
    ): {
      name: string;
      email: string;
      message: string;
      formType: FormType;
    } => ({
      name: values.name,
      email: values.email,
      message: `I would like to apply to join the Tiber Tutor team. I have ${values.years} years experience in IB science and scored ${values.score}/45 in the IB.<br/><br/>I am able to cover the subjects of ${values.subjects}.<br/><br/>My application for consideration is: ${values.application}`,
      formType: "application",
    }),
  });
  async function sendEmail() {
    setLoading(true);
    if (!executeRecaptcha) {
      setLoading(false);
      setNotification(
        "Contact",
        false,
        "Our system was not able to verify your human status. Please check you are connected to the internet. If so, try again now!",
        "error"
      );
      return;
    }

    const captchaToken = await executeRecaptcha("ApplicationFormSubmit");
    const res = await fetch("/api/strapi/sendCustomerEmail", {
      method: "POST",
      body: JSON.stringify({ ...form.getTransformedValues(), captchaToken }),
    });
    const response = await res.json();
    if (!res.ok) {
      setNotification(
        "Application",
        false,
        handleResponseError(response),
        "error"
      );
      setLoading(false);
      return;
    }
    setLoading(false);
    setSent(true);
  }
  return sent ? (
    <FormCompleteMsg theme={theme} />
  ) : (
    <Stack mt={"3rem"} gap={"xl"}>
      <FieldTextValue
        editing={true}
        formProps={{
          ...form.getInputProps("name"),
          placeholder: "Your name",
          withAsterisk: true,
        }}
        label="Full Name"
      />
      <FieldTextValue
        editing={true}
        formProps={{
          ...form.getInputProps("email"),
          placeholder: "Your email",
          withAsterisk: true,
        }}
        label="Email"
      />
      <Box>
        <FieldLabel label="Capable subjects" />
        <FieldMultiChipValue
          label="Capable subjects"
          hideLabel
          formProps={form.getInputProps("subjects")}
          subjectNames={subjectNames}
          editing={true}
        />
      </Box>
      <FieldNumberValue
        editing={true}
        formProps={{
          ...form.getInputProps("score"),
          placeholder: "/45",
          withAsterisk: true,
        }}
        label="IB Score"
      />
      <FieldNumberValue
        editing={true}
        formProps={{
          ...form.getInputProps("years"),
          placeholder: "Number of years of teaching",
          withAsterisk: true,
        }}
        label="Years of IB Experience"
      />
      <FieldTextareaValue
        editing={true}
        formProps={{
          ...form.getInputProps("application"),
          placeholder:
            "Please explain your interest, experience, accolades, and available commitment!",
          withAsterisk: true,
        }}
        label="Application"
        minRows={10}
        maxRows={25}
      />
      <Button
        name="Submit Application"
        color="darkblue"
        mt={"3rem"}
        loading={loading}
        disabled={!form.isValid()}
        ml={"auto"}
        mr={0}
        onClick={() => sendEmail()}
      >
        Submit Application
      </Button>
    </Stack>
  );
}

export function FormCompleteMsg({ theme }: { theme: MantineTheme }) {
  return (
    <Stack align="center" gap={0} mt={"3rem"}>
      <CheckCircleRounded
        style={{ width: 60 }}
        htmlColor={theme.colors.correct[6]}
      />
      <Text c="correct" fw={550} size={"md"}>
        Application sent. If we invite your for an interview, we will reply via
        email!
      </Text>
    </Stack>
  );
}
